import React, { type ComponentProps, type Key } from 'react';

import { noop } from '@adsk/alloy-react-helpers';
import { AlloySuspense } from '@adsk/alloy-react-suspense';

import {
  Token,
  Placeholder,
  DefaultFooter,
  Selected,
  MenuItem,
  SuspenseFallback,
  MenuGroupHeader,
} from '../layout';
import NoAutocompleteResults from '../layout/Menu/NoAutocompleteResults';
import { VirtualizedMenuGroupHeader } from '../templates';
import GroupCountLabel from '../templates/Menu/GroupCountLabel';
import type { FooterProps, RenderGroupHeaderProps } from '../types';
import { DefaultItem, GenericItem, ItemToString } from '../types';

export const defaultRenderToken = ({
  key,
  ...props
}: ComponentProps<typeof Token>) => <Token key={key} {...props} />;

export const defaultRenderPlaceholder = (
  props?: ComponentProps<typeof Placeholder>,
) => <Placeholder {...props} />;

export const defaultRenderFooter = ({
  selectedItems,
  disableClear = !selectedItems || selectedItems.length === 0,
  ...props
}: FooterProps) => (
  <DefaultFooter
    selectedItems={selectedItems}
    disableClear={disableClear}
    {...props}
  />
);

export const defaultRenderItem = <TItem extends GenericItem>({
  selectedItem,
  itemToString,
}: {
  selectedItem?: TItem | null;
  itemToString: ItemToString<TItem>;
}) =>
  selectedItem ? (
    <Selected selectedItems={[selectedItem]} itemToString={itemToString} />
  ) : null;

export const defaultRenderItems = <TItem extends GenericItem>({
  selectedItems,
  itemToString,
}: {
  selectedItems: TItem[];
  itemToString: ItemToString<TItem>;
}) => <Selected selectedItems={selectedItems} itemToString={itemToString} />;

export const defaultRenderMenuItem = <TItem extends GenericItem>({
  item,
  key,
  ...itemProps
}: {
  item: TItem;
  key?: Key;
}) => (
  <MenuItem
    key={key}
    text={(item as unknown as DefaultItem).label}
    subtext={(item as unknown as DefaultItem).subtext}
    {...itemProps}
  />
);

const renderGroupHeader = ({
  item,
  index,
  expandedGroupKey,
  allowCollapsibleGroups,
  isAutocomplete,
  setExpandedGroupKey,
  showGroupItemCount,
  headerComponent: HeaderComponent,
  start = 0,
  height = 0,
  scrollToViewVirtual = noop,
  isVisibleVirtual = () => false,
}: RenderGroupHeaderProps & {
  headerComponent: typeof MenuGroupHeader | typeof VirtualizedMenuGroupHeader;
}) => {
  return (
    <HeaderComponent
      allowCollapsibleGroups={
        allowCollapsibleGroups &&
        (!isAutocomplete || (item.filteredItemCount ?? 0) > 0)
      }
      onClick={() =>
        allowCollapsibleGroups &&
        (!isAutocomplete || (item.filteredItemCount ?? 0) > 0) &&
        setExpandedGroupKey?.(item.groupKey)
      }
      expanded={item.groupKey === expandedGroupKey}
      key={index}
      start={start}
      height={height}
      isVisibleVirtual={isVisibleVirtual}
      scrollToViewVirtual={scrollToViewVirtual}
    >
      {showGroupItemCount ? (
        !isAutocomplete || (item.filteredItemCount ?? 0) >= item.itemCount ? (
          `${item.label} (${item.itemCount})`
        ) : (
          <AlloySuspense fallback={<SuspenseFallback />}>
            <GroupCountLabel
              label={item.label}
              count={item.filteredItemCount ?? 0}
              total={item.itemCount}
            />
          </AlloySuspense>
        )
      ) : (
        item.label
      )}
    </HeaderComponent>
  );
};

export const defaultRenderGroupHeader = (props: RenderGroupHeaderProps) => {
  return renderGroupHeader({ ...props, headerComponent: MenuGroupHeader });
};

export const defaultRenderVirtualGroupHeader = (
  props: RenderGroupHeaderProps,
) => {
  return renderGroupHeader({
    ...props,
    headerComponent: VirtualizedMenuGroupHeader,
  });
};

export const defaultRenderNoAutocompleteResults = () => (
  <NoAutocompleteResults />
);
